import { useContext, useEffect, useMemo } from 'react';

import dayjs, { Dayjs } from 'dayjs';

import {
  ColSize,
  ContentSection,
  DatePickerForm,
  Form,
  RowSize,
} from '../../../../../../../shared/components';
import { FormInstance } from 'antd';
import { QuotationInfoContext } from '../../contexts';
import { LoadQuotationToRenewResponse } from '../../interfaces/load-renewal-quotation-response-interface';
import { LoadSavedQuotations } from '../../interfaces';
import { Article } from '../../../../../../../shared/interfaces/services/article-groups-service';
import { OPERATION_QUOTES_STATES } from '../../../../../SavedQuotes/shared/utils/mocks/states-saved-quotes-filter-form';

interface ValiditySectionProps {
  validitySectionForm: any;
  stepTwoForm?: FormInstance<any>;
  quotationForm?: { vigenciaDesde?: string; vigenciaHasta?: string };
}

export const ValiditySection = ({
  validitySectionForm,
  stepTwoForm,
  quotationForm,
}: ValiditySectionProps) => {  
    const {
      quotationToRenewal,
      savedQuotation,
      allArticleSelected
    } = useContext(QuotationInfoContext);
    const savedQuotationData = savedQuotation as LoadSavedQuotations | undefined;
    const tipoOperacion = savedQuotationData?.tipoOperacion 
      ? Number(savedQuotationData.tipoOperacion) 
      : null;

    const isRenewalFromSavedQuotation = tipoOperacion === Number(OPERATION_QUOTES_STATES.RENOVATION);
    const isRenewal = !!quotationToRenewal || isRenewalFromSavedQuotation;

    const article = allArticleSelected as Article | undefined;
    const diasSumar = article?.diasSumar ? Number(article.diasSumar) : 0;
    const diasRestar = article?.diasRestar ? Number(article.diasRestar) : 0;

    const dateRange = useMemo(() => {
      if (isRenewal) {
        return { minDate: undefined, maxDate: undefined };
      }

      const today = dayjs().startOf('day');
      const minDate = diasRestar > 0 ? today.subtract(diasRestar, 'days') : today;
      const maxDate = diasSumar > 0 ? today.add(diasSumar, 'days') : today;

      return { minDate, maxDate };
    }, [isRenewal, diasSumar, diasRestar]);

    const disabledDate = useMemo(() => {
      if (isRenewal) {
        return undefined;
      }

      return (current: Dayjs | null) => {
        if (!current) return false;
        
        const today = dayjs().startOf('day');
        const currentDate = current.startOf('day');
        
        if (diasRestar === 0 && currentDate.isBefore(today)) {
          return true;
        }
        
        if (diasSumar === 0 && currentDate.isAfter(today)) {
          return true;
        }
        
        const minDate = diasRestar > 0 ? today.subtract(diasRestar, 'days') : today;
        const maxDate = diasSumar > 0 ? today.add(diasSumar, 'days') : today;
        
        return currentDate.isBefore(minDate) || currentDate.isAfter(maxDate);
      };
    }, [isRenewal, diasSumar, diasRestar]);

    const handleCalendarChange = (
      dates: [Dayjs | null, Dayjs | null] | null,
      dateStrings: [string, string],
      info: { range?: 'start' | 'end' }
    ) => {
      if (!dates || !dates[0]) {
        return;
      }

      if (info.range === 'start' && dates[0]) {
        const fromDate = dates[0];
        const toDate = fromDate.add(1, 'year');
        
        validitySectionForm?.setFieldValue("validityDate", [fromDate, toDate]);
      }
    };

    const handleDateChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
      if (!dates || !dates[0]) {
        validitySectionForm?.setFieldValue("validityDate", null);
        return;
      }

      const fromDate = dates[0];
      const toDate = fromDate.add(1, 'year');
      
      setTimeout(() => {
        validitySectionForm?.setFieldValue("validityDate", [fromDate, toDate]);
      }, 0);
    };

const setValidityDate = () => {
  let from: Dayjs;
  let to: Dayjs;

  if (quotationToRenewal) {
    const quotation = quotationToRenewal as LoadQuotationToRenewResponse;
    from = dayjs(quotation.vigenciaDesde, "DD-MM-YYYY", true);
    to = dayjs(quotation.vigenciaHasta, "DD-MM-YYYY", true);
  } else if (savedQuotation) {
    const quotation = savedQuotation as LoadSavedQuotations;
    from = dayjs(quotation.vigenciaDesde, "DD-MM-YYYY", true);
    to = dayjs(quotation.vigenciaHasta, "DD-MM-YYYY", true);
  } else if (quotationForm?.vigenciaDesde && quotationForm?.vigenciaHasta) {
    from = dayjs(quotationForm.vigenciaDesde, 'YYYYMMDD');
    to = dayjs(quotationForm.vigenciaHasta, 'YYYYMMDD');
  } else {
    const fallback = [dayjs(), dayjs().add(1, "year")];
    const vigencyDate = stepTwoForm?.getFieldValue("vigencyDate");

    [from, to] = Array.isArray(vigencyDate) ? vigencyDate : fallback;
  }

  validitySectionForm?.setFieldValue("validityDate", [from, to]);
};
  useEffect(() => {    
    setValidityDate();
  }, [quotationToRenewal, savedQuotation, quotationForm]);

  return (
    <Form
      formProps={{
        form: validitySectionForm,
        name: 'validity-form',
      }}
    >
      <ContentSection level='h3' title='Vigencia'>
        <RowSize>
          <ColSize size={50}>
            <DatePickerForm
              isRangePicker
              formProps={{
                initialValue: [dayjs(), dayjs().add(1, 'years')],
                label: 'Vigencia',
                name: 'validityDate',
              }}
              rangePickerProps={{
                disabled: isRenewal,
                disabledDate: disabledDate,
                onChange: handleDateChange,
                onCalendarChange: handleCalendarChange,
                minDate: dateRange.minDate,
                maxDate: dateRange.maxDate,
              }}
            />
          </ColSize>
        </RowSize>
      </ContentSection>
    </Form>
  );
};
